/**
 * Events Index - Betrieb Müller (v2)
 *
 * Centralized access to all control events with filtering utilities.
 *
 * AGGREGATION RULE: If multiple objects trigger the same obligation,
 * generate exactly ONE aggregate event. Individual object-level events
 * MUST NOT appear in the feed in parallel.
 *
 * Event Distribution (17 total):
 * - NOW section (2): 1 urgent, 1 success
 * - NEXT MONTH section (2): 2 warning
 * - THIS SEASON section (8): 3 warning, 2 info, 3 success
 * - EARLIER section (5): 5 success
 */

import type { MockControlEvent, ControlCategory } from '@hofsync/design-system';

// Import all event collections
import {
  DUEV_CONTROL_EVENTS,
  EVENT_DUEV_PREP,
  EVENT_NAEHRSTOFFVERGLEICH,
  EVENT_STOFFSTROM_FRIST,
  EVENT_DUENGEPLANUNG_2026,
} from './duev-control';
import {
  EQUIPMENT_DOC_EVENTS,
  EVENT_TUEV_AGGREGATE,
  EVENT_SPRITZE_PRUFDIENST,
} from './equipment-docs';
import { EMPLOYEE_DOC_EVENTS, EVENT_SACHKUNDE_AGGREGATE } from './employee-docs';
import { SOIL_SAMPLE_EVENTS, EVENT_BODENPROBEN_NOW } from './soil-samples';
import {
  SUCCESS_STATE_EVENTS,
  EVENT_PFLANZENSCHUTZ_READY,
  EVENT_CC_READY,
  EVENT_ARBEITSSICHERHEIT_READY,
} from './success-states';
import {
  HISTORY_EVENTS,
  EVENT_HIST_TUEV_SCHLEPPER,
  EVENT_HIST_STOFFSTROM_2024,
  EVENT_HIST_UNTERWEISUNG_THOMAS,
  EVENT_HIST_DBE_2025,
  EVENT_HIST_CC_KONTROLLE,
} from './history';
import { DEADLINE_EVENTS, EVENT_AGRARDIESEL_FRIST } from './deadlines';

/**
 * All control events combined
 *
 * Note: v2 spec uses aggregate events only - no filtering needed.
 * The aggregate events (bodenproben, sachkunde, tuev-overview) are the
 * primary representation; no individual singles are included.
 */
export const ALL_CONTROL_EVENTS: MockControlEvent[] = [
  ...SOIL_SAMPLE_EVENTS,
  ...DUEV_CONTROL_EVENTS,
  ...EQUIPMENT_DOC_EVENTS,
  ...EMPLOYEE_DOC_EVENTS,
  ...SUCCESS_STATE_EVENTS,
  ...DEADLINE_EVENTS,
  ...HISTORY_EVENTS,
];

/**
 * Get all events sorted by timestamp (most recent first)
 */
export function getAllEventsSorted(): MockControlEvent[] {
  return [...ALL_CONTROL_EVENTS].sort((a, b) => {
    const dateA = new Date(a.timestamp).getTime();
    const dateB = new Date(b.timestamp).getTime();
    return dateB - dateA;
  });
}

/**
 * Get events by category
 */
export function getEventsByCategory(category: ControlCategory): MockControlEvent[] {
  return ALL_CONTROL_EVENTS.filter((event) => event.categories?.includes(category));
}

/**
 * Get events by priority
 */
export function getEventsByPriority(
  priority: 'urgent' | 'warning' | 'info' | 'success'
): MockControlEvent[] {
  return ALL_CONTROL_EVENTS.filter((event) => event.priority === priority);
}

/**
 * Get events that require attention (urgent or warning)
 */
export function getEventsRequiringAttention(): MockControlEvent[] {
  return ALL_CONTROL_EVENTS.filter(
    (event) => event.priority === 'urgent' || event.priority === 'warning'
  );
}

/**
 * Get success/resolved events
 */
export function getSuccessEvents(): MockControlEvent[] {
  return ALL_CONTROL_EVENTS.filter((event) => event.priority === 'success');
}

/**
 * Get event by ID
 */
export function getEventById(id: string): MockControlEvent | undefined {
  return ALL_CONTROL_EVENTS.find((event) => event.id === id);
}

/**
 * Category display names (German)
 */
export const CATEGORY_LABELS: Record<ControlCategory, string> = {
  duev: 'DÜV (Düngeverordnung)',
  'cross-compliance': 'Cross-Compliance',
  pflanzenschutz: 'Pflanzenschutz',
  tierhaltung: 'Tierhaltung',
  tuev: 'TÜV / Fahrzeugprüfung',
  sachkunde: 'Sachkundenachweise',
  bodenproben: 'Bodenproben',
  unterweisungen: 'Unterweisungen',
  fristen: 'Fristen & Termine',
};

/**
 * Get summary counts by category
 */
export function getCategorySummary(): Record<ControlCategory, { total: number; attention: number }> {
  const categories: ControlCategory[] = [
    'duev',
    'cross-compliance',
    'pflanzenschutz',
    'tierhaltung',
    'tuev',
    'sachkunde',
    'bodenproben',
    'unterweisungen',
    'fristen',
  ];

  const summary: Record<ControlCategory, { total: number; attention: number }> = {} as Record<
    ControlCategory,
    { total: number; attention: number }
  >;

  categories.forEach((cat) => {
    const events = getEventsByCategory(cat);
    summary[cat] = {
      total: events.length,
      attention: events.filter((e) => e.priority === 'urgent' || e.priority === 'warning').length,
    };
  });

  return summary;
}

// Re-export individual events for direct access
export {
  // Soil samples (NOW)
  EVENT_BODENPROBEN_NOW,
  SOIL_SAMPLE_EVENTS,
  // DÜV (single control readiness + deadlines)
  EVENT_DUEV_PREP,
  EVENT_NAEHRSTOFFVERGLEICH,
  EVENT_STOFFSTROM_FRIST,
  EVENT_DUENGEPLANUNG_2026,
  DUEV_CONTROL_EVENTS,
  // Equipment (aggregate only)
  EVENT_TUEV_AGGREGATE,
  EVENT_SPRITZE_PRUFDIENST,
  EQUIPMENT_DOC_EVENTS,
  // Employees (THIS SEASON)
  EVENT_SACHKUNDE_AGGREGATE,
  EMPLOYEE_DOC_EVENTS,
  // Success states (NOW + THIS SEASON)
  EVENT_PFLANZENSCHUTZ_READY,
  EVENT_CC_READY,
  EVENT_ARBEITSSICHERHEIT_READY,
  SUCCESS_STATE_EVENTS,
  // Deadlines (NOW)
  EVENT_AGRARDIESEL_FRIST,
  DEADLINE_EVENTS,
  // History (EARLIER)
  EVENT_HIST_TUEV_SCHLEPPER,
  EVENT_HIST_STOFFSTROM_2024,
  EVENT_HIST_UNTERWEISUNG_THOMAS,
  EVENT_HIST_DBE_2025,
  EVENT_HIST_CC_KONTROLLE,
  HISTORY_EVENTS,
};
